import { ref, computed } from 'vue';
import { projectService } from '@/services/projectService';
import { useToastStore } from '@/store/toastStore';

/**
 * Composable for managing projects
 */
export function useProjects() {
  const projects = ref([]);
  const loading = ref(false);
  const error = ref(null);
  const pagination = ref({
    page: 1,
    limit: 50,
    total: 0,
  });
  const filters = ref({
    status: null,
    search: '',
    dateFrom: null,
    dateTo: null,
  });

  const toastStore = useToastStore();

  /**
   * Fetch all projects
   *
   * @param {object} params  query overrides
   * @param {boolean} append append results to the existing list instead of
   *                         replacing it (used voor infinite scroll op mobiel)
   */
  const fetchProjects = async (params = {}, append = false) => {
    loading.value = true;
    error.value = null;

    try {
      const queryParams = {
        page: pagination.value.page,
        limit: pagination.value.limit,
        ...filters.value,
        ...params,
      };

      // Remove null/empty filters
      Object.keys(queryParams).forEach((key) => {
        if (queryParams[key] === null || queryParams[key] === '') {
          delete queryParams[key];
        }
      });

      const response = await projectService.getAllProjects(queryParams);
      const newItems = response.projects || response.data || [];
      projects.value = append ? [...projects.value, ...newItems] : newItems;
      pagination.value = {
        ...pagination.value,
        ...(response.pagination || {}),
      };
    } catch (err) {
      error.value = err.message || 'Failed to fetch projects';
      toastStore.addToast({
        type: 'error',
        message: error.value,
      });
    } finally {
      loading.value = false;
    }
  };

  /**
   * Fetch a single project by ID
   */
  const fetchProject = async (projectId) => {
    loading.value = true;
    error.value = null;

    try {
      const project = await projectService.getProjectById(projectId);
      return project;
    } catch (err) {
      error.value = err.message || 'Failed to fetch project';
      toastStore.addToast({
        type: 'error',
        message: error.value,
      });
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Create a new project
   */
  const createProject = async (projectData) => {
    loading.value = true;
    error.value = null;

    try {
      const project = await projectService.createProject(projectData);
      toastStore.addToast({
        type: 'success',
        message: 'Project successfully created',
      });
      return project;
    } catch (err) {
      error.value = err.message || 'Failed to create project';
      toastStore.addToast({
        type: 'error',
        message: error.value,
      });
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Update a project
   */
  const updateProject = async (projectId, projectData) => {
    loading.value = true;
    error.value = null;

    try {
      const project = await projectService.updateProject(projectId, projectData);
      toastStore.addToast({
        type: 'success',
        message: 'Project successfully updated',
      });
      return project;
    } catch (err) {
      error.value = err.message || 'Failed to update project';
      toastStore.addToast({
        type: 'error',
        message: error.value,
      });
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Delete a project
   */
  const deleteProject = async (projectId) => {
    loading.value = true;
    error.value = null;

    try {
      await projectService.deleteProject(projectId);
      toastStore.addToast({
        type: 'success',
        message: 'Project successfully deleted',
      });
      // Remove from list
      projects.value = projects.value.filter((p) => p.id !== projectId);
    } catch (err) {
      error.value = err.message || 'Failed to delete project';
      toastStore.addToast({
        type: 'error',
        message: error.value,
      });
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Complete a project
   */
  const completeProject = async (projectId) => {
    loading.value = true;
    error.value = null;

    try {
      const project = await projectService.completeProject(projectId);
      toastStore.addToast({
        type: 'success',
        message: 'Project marked as complete',
      });
      return project;
    } catch (err) {
      error.value = err.message || 'Failed to complete project';
      toastStore.addToast({
        type: 'error',
        message: error.value,
      });
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Fetch project by deal ID
   * @param {string} dealId - Deal ID
   * @returns {Promise<Object|null>} Project or null
   */
  const fetchProjectByDeal = async (dealId) => {
    loading.value = true;
    error.value = null;

    try {
      const project = await projectService.getProjectByDealId(dealId);
      return project;
    } catch (err) {
      error.value = err.message || 'Failed to fetch project for deal';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Reopen a completed project (for meerwerk)
   */
  const reopenProject = async (projectId) => {
    loading.value = true;
    error.value = null;

    try {
      const project = await projectService.reopenProject(projectId);
      toastStore.addToast({
        type: 'success',
        message: 'Project heropend',
      });
      return project;
    } catch (err) {
      error.value = err.message || 'Failed to reopen project';
      toastStore.addToast({
        type: 'error',
        message: error.value,
      });
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Update filters
   */
  const updateFilters = (newFilters) => {
    filters.value = { ...filters.value, ...newFilters };
    pagination.value.page = 1; // Reset to first page
    fetchProjects();
  };

  /**
   * Clear filters
   */
  const clearFilters = () => {
    filters.value = {
      status: null,
      search: '',
      dateFrom: null,
      dateTo: null,
    };
    pagination.value.page = 1;
    fetchProjects();
  };

  return {
    projects,
    loading,
    error,
    pagination,
    filters,
    fetchProjects,
    fetchProject,
    fetchProjectByDeal,
    createProject,
    updateProject,
    deleteProject,
    completeProject,
    reopenProject,
    updateFilters,
    clearFilters,
  };
}
